import { EventRegistration } from './models';

/**
 * The addresses to reach a set of registrants on, in the order they were given.
 *
 * `contactEmail` wins over `email` — the same precedence the registration rows already use for
 * their own mailto links. It is not a cosmetic preference: the nominated address is the one the
 * registrant chose for this event, and it exists because an account email can be an Apple or
 * Google private relay that they never read.
 *
 * Deduplicated case-insensitively, because two registrations can resolve to the same inbox when
 * one nominates what another uses as its account address, and an organiser should not mail the
 * same person twice.
 */
export const toRecipientList = (registrations: EventRegistration[] | null | undefined): string[] => {
    const seen = new Set<string>();

    return (registrations || []).reduce<string[]>((addresses, registration) => {
        const address = (registration?.contactEmail || registration?.email || '').trim().toLowerCase();
        if (!address || seen.has(address)) {
            return addresses;
        }
        seen.add(address);
        return [...addresses, address];
    }, []);
};

/**
 * Mail clients cap a mailto: URL at roughly 2000 characters and truncate what will not fit
 * **silently** — an organiser would send to some of their players and have no way of knowing.
 * Past this many addresses the UI offers copying instead, which has no such limit.
 */
export const MAX_MAILTO_RECIPIENTS = 50;

/** A BCC mailto for these recipients. BCC so that no player learns another player's address. */
export const toBccMailto = (recipients: string[], subject?: string): string => {
    const query = [`bcc=${encodeURIComponent(recipients.join(','))}`];
    if (subject) {
        query.push(`subject=${encodeURIComponent(subject)}`);
    }
    return `mailto:?${query.join('&')}`;
};
